import React from "react";
import Container from "react-bootstrap/Container";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import Loader from "@shared/components/Loader";
import { useAxiosGet } from "@features/programs/services/apiService";
import {
  badges,
  programMilestones,
  isProgramActive,
} from "@features/programs/utils/gamification";
import { ProgramPath, BadgeToken } from "./gamification";
import "./gamification/gamification.css";

/**
 * Meilensteine: the path of every active program and the seven badges, the
 * earned ones with their date, the open ones with what they take. Every
 * badge states its condition up front — there is nothing to discover and no
 * one to compare against.
 */
function Milestones() {
  const { t, i18n } = useTranslation();
  const programs = useAxiosGet("/:locale/v2/:token/programs/summary");

  if (programs.loading) return <Loader />;
  if (programs.error) {
    return (
      <Container fluid className="gm gm-page">
        <p>{t("errors.generic", { defaultValue: "Ein Fehler ist aufgetreten. Bitte versuche es noch einmal" })}</p>
      </Container>
    );
  }

  const list = programs.data || [];
  const active = list.filter((program) => isProgramActive(program));
  const all = badges(list);
  const earned = all.filter((badge) => badge.earned);
  const open = all.filter((badge) => !badge.earned);

  const formatDate = (date) =>
    date ? date.toLocaleDateString(i18n.language, { day: "numeric", month: "long" }) : "";

  const badgeTile = (badge) => (
    <div key={badge.id} className={`gm-badge${badge.earned ? "" : " gm-badge-locked"}`}>
      <BadgeToken id={badge.id} locked={!badge.earned} />
      <span className="gm-badge-name">{t(`gamification.badges.${badge.id}.name`)}</span>
      <span className="gm-badge-meta">
        {badge.earned ? formatDate(badge.earnedAt) : t(`gamification.badges.${badge.id}.criterion`)}
      </span>
      {!badge.earned && badge.progress && (
        <span className="gm-badge-meta">
          {t("gamification.badges.progress", badge.progress)}
        </span>
      )}
    </div>
  );

  return (
    <Container fluid className="gm gm-page">
      <h1 className="gm-page-title">{t("gamification.milestones.title")}</h1>
      <p className="gm-page-lead">{t("gamification.milestones.lead")}</p>

      {active.length === 0 && <p className="gm-page-lead">{t("gamification.milestones.no_programs")}</p>}

      {active.map((program) => {
        const milestones = programMilestones(program);
        return (
          <div key={program.id} className="gm-card gm-path-card">
            <p className="gm-section-label">
              {parse(String(program.name || ""))} ·{" "}
              {t("gamification.path.sessions", { done: milestones.done, planned: milestones.planned })}
            </p>
            <ProgramPath milestones={milestones} direction="horizontal" />
          </div>
        );
      })}

      <section className="gm-section">
        <div className="gm-section-head">
          <h2>{t("gamification.badges.earned")}</h2>
          <span>{earned.length}</span>
        </div>
        {earned.length > 0 && <div className="gm-badge-grid">{earned.map(badgeTile)}</div>}
      </section>

      {open.length > 0 && (
        <section className="gm-section">
          <div className="gm-section-head">
            <h2>{t("gamification.badges.open")}</h2>
            <span>{open.length}</span>
          </div>
          <div className="gm-badge-grid">{open.map(badgeTile)}</div>
        </section>
      )}
    </Container>
  );
}

export default Milestones;
